import type { DraggableAttributes, UniqueIdentifier } from '@dnd-kit/core';
import { DragOverlay } from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { CSSInterpolation } from '@emotion/serialize';
import type { ReactNode } from 'react';
import { memo } from 'react';

import { useDelayUnmount } from '@adflow/frontend/util-common';

interface DraggableProps {
	children: (
		attributes?: DraggableAttributes,
		listeners?: SyntheticListenerMap,
		isDragging?: boolean
	) => ReactNode;
	id: UniqueIdentifier;
	dropAnimation?: boolean;
	hideDraggable?: boolean;
	dragOpacity?: '0%' | '30%' | '100%';
	disabled?: boolean;
	containerStyle?: CSSInterpolation;
}

export const Draggable = memo(function DraggableRender({
	id,
	dropAnimation = true,
	hideDraggable,
	dragOpacity = '0%',
	disabled,
	containerStyle,
	...props
}: DraggableProps) {
	const {
		setNodeRef,
		attributes,
		listeners,
		transition,
		transform,
		isDragging,
	} = useSortable({ id, disabled });

	const shouldRender = useDelayUnmount(isDragging, 250) && !hideDraggable;

	// Needed to override role="button" which is preventing 'cursor: grab' from working
	attributes.role = 'draggable';

	return (
		<>
			<div
				ref={setNodeRef}
				css={containerStyle}
				// Set draggable properties on `style` prop to avoid
				// creating unnecessary intermediate classes
				style={{
					transition,
					transform: CSS.Translate.toString(transform),
					opacity: isDragging ? dragOpacity : '100%',
					cursor: disabled ? 'auto' : 'grab',
				}}
			>
				{props.children(attributes, listeners, isDragging)}
			</div>
			{shouldRender ? (
				<DragOverlay
					dropAnimation={dropAnimation ? { sideEffects: null } : null}
				>
					{isDragging ? props.children(undefined, undefined, isDragging) : null}
				</DragOverlay>
			) : null}
		</>
	);
});
